/**
 * VoiceCallHistoryPage
 *
 * Filtered, paginated table of voice calls.
 * Server-side filters: direction, sentiment (via usePaginatedQuery).
 * Client-side filters: agentId, outcome, search (applied to fetched results).
 */

// LOCAL_TYPE_JUSTIFIED: TCallFilterState and TCallFilterAction are page-local useReducer types defined in src/@types/voiceAgents.ts — imported below

import { useReducer, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useVoiceCallHistory } from '@/hooks/useVoiceCalls';
import { useVoiceAgentsList } from '@/hooks/useVoiceAgents';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PhoneIncoming, PhoneOutgoing, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  TCallDirection,
  TCallSentiment,
  TCallOutcome,
  ICallHistoryFilterState,
  TCallHistoryFilterAction,
} from '@/@types/voiceAgents';

// --- Constants ---

const CALL_DIRECTIONS: TCallDirection[] = ['inbound', 'outbound'];
const CALL_SENTIMENTS: TCallSentiment[] = ['positive', 'neutral', 'negative'];
const CALL_OUTCOMES: TCallOutcome[] = ['resolved', 'escalated', 'callback_needed', 'no_action'];

// --- Badge Variant Maps ---

const SENTIMENT_VARIANTS: Record<TCallSentiment, 'default' | 'secondary' | 'destructive'> = {
  positive: 'default',
  neutral: 'secondary',
  negative: 'destructive',
};

const OUTCOME_VARIANTS: Record<TCallOutcome, 'default' | 'secondary' | 'outline'> = {
  resolved: 'default',
  escalated: 'secondary',
  callback_needed: 'outline',
  no_action: 'secondary',
};

// --- Filter Reducer ---

const INITIAL_FILTER_STATE: ICallHistoryFilterState = {
  search: '',
  agentId: 'all',
  direction: 'all',
  sentiment: 'all',
  outcome: 'all',
};

function filterReducer(
  state: ICallHistoryFilterState,
  action: TCallHistoryFilterAction
): ICallHistoryFilterState {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...state, search: action.payload };
    case 'SET_AGENT':
      return { ...state, agentId: action.payload };
    case 'SET_DIRECTION':
      return { ...state, direction: action.payload };
    case 'SET_SENTIMENT':
      return { ...state, sentiment: action.payload };
    case 'SET_OUTCOME':
      return { ...state, outcome: action.payload };
  }
}

// --- Type Guard Helpers (no `as` assertions) ---

function toDirection(value: string): TCallDirection | undefined {
  return CALL_DIRECTIONS.find(d => d === value);
}

function toSentiment(value: string): TCallSentiment | undefined {
  return CALL_SENTIMENTS.find(s => s === value);
}

function toOutcome(value: string): TCallOutcome | undefined {
  return CALL_OUTCOMES.find(o => o === value);
}

// --- Format Helpers ---

function formatDate(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
}

// --- Component ---

export function VoiceCallHistoryPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const [filters, dispatch] = useReducer(filterReducer, INITIAL_FILTER_STATE);

  const { data: agents } = useVoiceAgentsList(currentOrg?._id);

  const serverDirection = toDirection(filters.direction);
  const serverSentiment = toSentiment(filters.sentiment);

  const {
    results: rawCalls,
    isLoading: callsLoading,
    loadMore,
    status,
  } = useVoiceCallHistory(currentOrg?._id, {
    direction: serverDirection,
    sentiment: serverSentiment,
  });

  const isLoading = orgLoading || callsLoading;

  const agentNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of agents ?? []) {
      map.set(a._id, a.name);
    }
    return map;
  }, [agents]);

  const filteredCalls = useMemo(() => {
    let result = rawCalls ?? [];

    if (filters.agentId !== 'all') {
      result = result.filter(c => c.agentId === filters.agentId);
    }

    const outcomeFilter = toOutcome(filters.outcome);
    if (outcomeFilter !== undefined) {
      result = result.filter(c => c.outcome === outcomeFilter);
    }

    if (filters.search.trim()) {
      const q = filters.search.toLowerCase();
      result = result.filter(c => c.callerNumber.toLowerCase().includes(q));
    }

    return result;
  }, [rawCalls, filters.agentId, filters.outcome, filters.search]);

  const handleRowClick = useCallback(
    (callId: string) => navigate(`/insurance/voice-agents/calls/${callId}`),
    [navigate]
  );

  const handleLoadMore = useCallback(() => loadMore(25), [loadMore]);

  // Guard: no org
  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Phone className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to view call history.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Call History</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          {isLoading
            ? 'Loading...'
            : `${filteredCalls.length} call${filteredCalls.length !== 1 ? 's' : ''}`}
        </p>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input
          placeholder="Search caller number..."
          value={filters.search}
          onChange={e => dispatch({ type: 'SET_SEARCH', payload: e.target.value })}
          className="max-w-xs"
        />
        <Select
          value={filters.agentId}
          onValueChange={v => dispatch({ type: 'SET_AGENT', payload: v })}
        >
          <SelectTrigger className={cn('w-40')}>
            <SelectValue placeholder="All Agents" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Agents</SelectItem>
            {(agents ?? []).map(a => (
              <SelectItem key={a._id} value={a._id}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.direction}
          onValueChange={v => {
            const dir = toDirection(v);
            dispatch({ type: 'SET_DIRECTION', payload: dir ?? 'all' });
          }}
        >
          <SelectTrigger className={cn('w-32')}>
            <SelectValue placeholder="Direction" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="inbound">Inbound</SelectItem>
            <SelectItem value="outbound">Outbound</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.sentiment}
          onValueChange={v => {
            const sent = toSentiment(v);
            dispatch({ type: 'SET_SENTIMENT', payload: sent ?? 'all' });
          }}
        >
          <SelectTrigger className={cn('w-32')}>
            <SelectValue placeholder="Sentiment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="positive">Positive</SelectItem>
            <SelectItem value="neutral">Neutral</SelectItem>
            <SelectItem value="negative">Negative</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.outcome}
          onValueChange={v => {
            const out = toOutcome(v);
            dispatch({ type: 'SET_OUTCOME', payload: out ?? 'all' });
          }}
        >
          <SelectTrigger className={cn('w-44')}>
            <SelectValue placeholder="Outcome" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="escalated">Escalated</SelectItem>
            <SelectItem value="callback_needed">Callback Needed</SelectItem>
            <SelectItem value="no_action">No Action</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-36">Date/Time</TableHead>
                <TableHead className="w-10" />
                <TableHead>Caller</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead className="text-right w-24">Duration</TableHead>
                <TableHead className="w-28">Sentiment</TableHead>
                <TableHead className="w-36">Outcome</TableHead>
                <TableHead className="w-24">Trust</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-16 text-muted-foreground">
                    Loading calls...
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && filteredCalls.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-16">
                    <Phone className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">No calls match your filters</p>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                filteredCalls.map(call => (
                  <TableRow
                    key={call._id}
                    className={cn('cursor-pointer hover:bg-muted/20')}
                    onClick={() => handleRowClick(call._id)}
                  >
                    <TableCell className="text-sm">{formatDate(call.startedAt)}</TableCell>
                    <TableCell>
                      {call.direction === 'inbound' ? (
                        <PhoneIncoming className="w-4 h-4 text-green-500" />
                      ) : (
                        <PhoneOutgoing className="w-4 h-4 text-blue-500" />
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{call.callerNumber}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {agentNameMap.get(call.agentId) ?? '—'}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {call.durationMs !== undefined ? formatDuration(call.durationMs) : '—'}
                    </TableCell>
                    <TableCell>
                      {call.sentiment !== undefined && (
                        <Badge variant={SENTIMENT_VARIANTS[call.sentiment]} className="capitalize">
                          {call.sentiment}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {call.outcome !== undefined && (
                        <Badge variant={OUTCOME_VARIANTS[call.outcome]} className="capitalize">
                          {call.outcome.replace('_', ' ')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {call.callerTrustLevel}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Load More */}
      {status === 'CanLoadMore' && (
        <div className="flex justify-center mt-4">
          <Button variant="outline" onClick={handleLoadMore}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
